/**
 * ChartCommunicationThread — Unified iMessage-style conversation component
 *
 * - Me (sender_id === currentUser.id) → right side, primary bubble
 * - Others → left side, gray bubble with name + role badge
 * - Messages immutable once sent — no edit button
 * - Priority badge + origin tag in header
 * - "Mark Resolved" is a soft milestone, NOT a close
 * - Adding a reply to a resolved thread auto-reopens it
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Send, Check, X, ChevronDown, Phone, ClipboardList, Heart,
  MessageSquare, Stethoscope, AlertCircle, Flag, User, Plus,
  MoreHorizontal, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// ── Config ───────────────────────────────────────────────────────────────────

const ORIGIN_CONFIG = {
  phone:   { label: 'Phone Call',   icon: Phone,         color: 'bg-blue-50 text-blue-700 border-blue-200' },
  log:     { label: 'Log Note',     icon: ClipboardList, color: 'bg-gray-50 text-gray-600 border-gray-200' },
  nursing: { label: 'Nursing Note', icon: Heart,         color: 'bg-teal-50 text-teal-700 border-teal-200' },
  team:    { label: 'Team Message', icon: MessageSquare, color: 'bg-violet-50 text-violet-700 border-violet-200' },
  visit:   { label: 'Visit Note',   icon: Stethoscope,   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const PRIORITY_CONFIG = {
  urgent:  { label: 'Urgent',  color: 'bg-red-100 text-red-700 border-red-300',     dot: 'bg-red-500' },
  routine: { label: 'Routine', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  fyi:     { label: 'FYI',     color: 'bg-gray-50 text-gray-500 border-gray-200',    dot: 'bg-gray-300' },
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function RoleBadge({ role }) {
  const colors = {
    provider: 'bg-blue-100 text-blue-700',
    nurse:    'bg-teal-100 text-teal-700',
    front_desk: 'bg-purple-100 text-purple-700',
    office_manager: 'bg-orange-100 text-orange-700',
    admin: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded ${colors[role] || 'bg-gray-100 text-gray-600'}`}>
      {role?.replace(/_/g, ' ')}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export function ChartCommunicationThread({
  conversation,           // The conversation object (with .messages populated)
  onUpdated,              // Callback when conversation state changes
  compact = false,        // Compact mode for list view
  autoFocus = false,
}) {
  const { user } = useAuth();
  const myId = user?.id || user?.user_id || '';

  const [messages, setMessages] = useState(conversation?.messages || []);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [targetUser, setTargetUser] = useState(null); // { id, name, role }
  const [staffList, setStaffList] = useState([]);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const conv = conversation;
  const origin = ORIGIN_CONFIG[conv?.origin_tag] || ORIGIN_CONFIG.team;
  const priority = PRIORITY_CONFIG[conv?.priority] || PRIORITY_CONFIG.routine;
  const OriginIcon = origin.icon;
  const isResolved = conv?.status === 'resolved';

  // Load staff for recipient picker
  useEffect(() => {
    api.get('/conversations/staff/recipients').then(r => setStaffList(r.data.staff || [])).catch(() => {});
  }, []);

  // Load messages if not pre-populated
  useEffect(() => {
    if (conv?.id && (!conv.messages || conv.messages.length === 0)) {
      api.get(`/conversations/${conv.id}`).then(r => {
        setMessages(r.data.conversation?.messages || []);
      }).catch(() => {});
    } else {
      setMessages(conv?.messages || []);
    }
  }, [conv?.id]); // eslint-disable-line

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) textareaRef.current.focus();
  }, [autoFocus]);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/conversations/${conv.id}/reply`, {
        body: replyText.trim(),
        target_user_id: targetUser?.id || null,
        target_user_name: targetUser?.name || '',
      });
      const newMsg = res.data.message;
      setMessages(prev => [...prev, { ...newMsg, immutable: true }]);
      setReplyText('');
      setTargetUser(null);
      if (onUpdated) onUpdated();
      toast.success(targetUser ? `Sent to ${targetUser.name}` : 'Message sent');
    } catch { toast.error('Failed to send'); }
    finally { setSubmitting(false); }
  };

  const handleMarkResolved = async () => {
    setResolving(true);
    try {
      await api.post(`/conversations/${conv.id}/resolve`, { resolution_note: '' });
      toast.success('Marked as resolved — thread stays open for future additions');
      if (onUpdated) onUpdated();
    } catch { toast.error('Failed'); }
    finally { setResolving(false); }
  };

  const handleUpdatePriority = async (p) => {
    try {
      await api.put(`/conversations/${conv.id}/priority`, { priority: p });
      setShowPriorityPicker(false);
      if (onUpdated) onUpdated();
      toast.success(`Priority set to ${p}`);
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="flex flex-col h-full" data-testid={`thread-${conv?.id}`}>
      {/* Thread Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-card shrink-0">
        {/* Origin tag */}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${origin.color}`}>
          <OriginIcon className="w-3 h-3" />
          {origin.label}
        </span>

        {/* Priority with picker */}
        <div className="relative">
          <button
            onClick={() => setShowPriorityPicker(p => !p)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${priority.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
            {priority.label}
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
          {showPriorityPicker && (
            <div className="absolute left-0 top-full mt-1 z-20 bg-card border rounded-lg shadow-lg py-1 w-32">
              {Object.entries(PRIORITY_CONFIG).map(([k, cfg]) => (
                <button key={k} onClick={() => handleUpdatePriority(k)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-accent">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="text-sm font-semibold flex-1 truncate">{conv?.title}</span>

        {/* Resolved badge */}
        {isResolved && (
          <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 gap-0.5">
            <Check className="w-2.5 h-2.5" /> Resolved
          </Badge>
        )}
        {isResolved && (
          <span className="text-[9px] text-muted-foreground italic">Thread open for additions</span>
        )}
      </div>

      {/* Messages — iMessage style */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground italic">
            No messages yet
          </div>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.sender_id === myId;
          const isResolutionMsg = msg.message_type === 'resolution';
          return (
            <div key={msg.id || i}
              className={`flex ${isMe ? 'justify-end' : 'justify-start'} gap-2`}
              data-testid={`message-${i}`}>
              {/* Avatar for others */}
              {!isMe && (
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-auto">
                  <span className="text-[9px] font-semibold text-muted-foreground">
                    {(msg.sender_name || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className={`flex flex-col max-w-[72%] ${isMe ? 'items-end' : 'items-start'}`}>
                {/* Sender name + role (others only) */}
                {!isMe && (
                  <div className="flex items-center gap-1.5 mb-0.5 px-1">
                    <span className="text-[11px] font-semibold text-foreground">{msg.sender_name}</span>
                    <RoleBadge role={msg.sender_role} />
                  </div>
                )}
                {/* Targeted reply indicator */}
                {msg.target_user_name && (
                  <div className={`text-[9px] text-muted-foreground mb-0.5 px-1 flex items-center gap-1 ${isMe ? 'justify-end' : ''}`}>
                    <span>→ {msg.target_user_name}</span>
                  </div>
                )}
                {/* Bubble */}
                <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  isResolutionMsg
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-[11px] italic'
                    : isMe
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-muted text-foreground rounded-bl-sm'
                }`}>
                  {isResolutionMsg && <Check className="w-3 h-3 inline mr-1" />}
                  {msg.body}
                </div>
                {/* Timestamp — always shown, immutable */}
                <span className="text-[9px] text-muted-foreground mt-0.5 px-1">
                  {formatTime(msg.timestamp)}
                  {msg.immutable && isMe && (
                    <span className="ml-1 text-muted-foreground/50">· sent</span>
                  )}
                </span>
              </div>
              {/* Avatar for me */}
              {isMe && (
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-auto">
                  <span className="text-[9px] font-semibold text-primary">
                    {(user?.name || 'Me').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply Area */}
      <div className="px-4 py-3 border-t bg-card space-y-2 shrink-0">
        {/* Reply-to row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <button
              onClick={() => setShowRecipientPicker(p => !p)}
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="recipient-picker-btn">
              <User className="w-3 h-3" />
              {targetUser ? (
                <>
                  <span className="font-medium text-foreground">{targetUser.name}</span>
                  <RoleBadge role={targetUser.role} />
                  <button onClick={(e) => { e.stopPropagation(); setTargetUser(null); }}
                    className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                </>
              ) : (
                <span>Replying to team <ChevronDown className="w-2.5 h-2.5 inline" /></span>
              )}
            </button>
            {showRecipientPicker && (
              <div className="absolute bottom-full left-0 mb-1 z-30 w-56 bg-card border rounded-xl shadow-xl py-1.5 animate-in fade-in-0 slide-in-from-bottom-2 duration-100"
                data-testid="recipient-picker-dropdown">
                <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-1.5">
                  Reply to specific person
                </p>
                <button className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-[11px]"
                  onClick={() => { setTargetUser(null); setShowRecipientPicker(false); }}>
                  <MessageSquare className="w-3 h-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Whole team</span>
                </button>
                <div className="border-t my-1" />
                {staffList.map(s => (
                  <button key={s.id}
                    onClick={() => { setTargetUser(s); setShowRecipientPicker(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-[11px] ${s.is_primary_mapped ? 'bg-accent/30' : ''}`}
                    data-testid={`recipient-${s.id}`}>
                    <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[8px] font-bold shrink-0">
                      {s.name.charAt(0)}
                    </div>
                    <div className="flex-1 text-left">
                      <span className="font-medium">{s.name}</span>
                      {s.is_primary_mapped && <span className="ml-1 text-[8px] text-violet-600">mapped</span>}
                    </div>
                    <RoleBadge role={s.role} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Resolve button */}
          {!isResolved && (
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
              onClick={handleMarkResolved} disabled={resolving} data-testid="mark-resolved-btn">
              {resolving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Mark Resolved
            </Button>
          )}
          {isResolved && (
            <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200 shrink-0">
              Resolved · still open for additions
            </Badge>
          )}
        </div>

        {/* Text input + send */}
        <div className="flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            placeholder={targetUser ? `Message ${targetUser.name}...` : 'Add to thread...'}
            className="flex-1 resize-none text-sm min-h-[44px] max-h-[120px]"
            rows={1}
            data-testid="reply-textarea"
          />
          <Button
            size="sm"
            className="h-10 w-10 p-0 rounded-xl shrink-0"
            onClick={handleSend}
            disabled={submitting || !replyText.trim()}
            data-testid="send-btn">
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { ORIGIN_CONFIG, PRIORITY_CONFIG };
